import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { AlertTriangle, Home, Loader2, Smartphone } from "lucide-react";
import { useEffect, useState } from "react";
import { FaApple, FaGooglePlay } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const AppRedirection = () => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);
  const [detectedPlatform, setDetectedPlatform] = useState<string | null>(null);
  const [isRedirecting, setIsRedirecting] = useState(false);

  useEffect(() => {
    const detectPlatform = () => {
      const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera || '';

      const deviceDetection = {
        isAndroid: /Android/i.test(userAgent) || /Linux; Android/i.test(userAgent),
        isIOS: /iPhone|iPad|iPod/i.test(userAgent) || (/Macintosh/i.test(userAgent) && 'ontouchend' in document),
        isWindowsPhone: /Windows Phone/i.test(userAgent) || /IEMobile/i.test(userAgent)
      };

      const isMobileDevice = deviceDetection.isAndroid || deviceDetection.isIOS || deviceDetection.isWindowsPhone ||
        (window.matchMedia && window.matchMedia("(max-width: 768px)").matches) ||
        ('ontouchstart' in window || navigator.maxTouchPoints > 0);

      setIsMobile(isMobileDevice);

      let platform: string | null = null;
      if (deviceDetection.isIOS) {
        platform = 'ios';
      } else if (deviceDetection.isAndroid || deviceDetection.isWindowsPhone) {
        platform = 'android';
      }
      setDetectedPlatform(platform);

      // Auto-redirect if mobile and platform is detected
      if (isMobileDevice && platform) {
        setIsRedirecting(true);

        if (platform === 'android') {
          // Get current URL parameters to pass along
          const urlParams = new URLSearchParams(window.location.search);
          const token = urlParams.get('token') || '';
          const intentUrl = `intent://login-app?token=${encodeURIComponent(token)}#Intent;scheme=kaliamobileapp;package=de.kalialab.kaliaapp;S.browser_fallback_url=https%3A//play.google.com/store/apps/details%3Fid%3Dde.kalialab.kaliaapp;end`;
          window.location.href = intentUrl;
        } else {
          // iOS
          const urlParams = new URLSearchParams(window.location.search);
          const token = urlParams.get('token') || '';
          window.location.href = `kaliamobileapp://login-app?token=${encodeURIComponent(token)}`;
        }
      }
    };

    detectPlatform();
  }, []);

  const handleManualRedirect = (platform: 'ios' | 'android') => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token') || '';

    if (platform === 'android') {
      const intentUrl = `intent://login-app?token=${encodeURIComponent(token)}#Intent;scheme=kaliamobileapp;package=de.kalialab.kaliaapp;S.browser_fallback_url=https%3A//play.google.com/store/apps/details%3Fid%3Dde.kalialab.kaliaapp;end`;
      window.location.href = intentUrl;
    } else {
      window.location.href = `kaliamobileapp://login-app?token=${encodeURIComponent(token)}`;
      setTimeout(() => {
        window.open('https://apps.apple.com/app/KD259G8M9Z', '_blank');
      }, 1000);
    }
  };

  // Show loading state while redirecting
  if (isRedirecting) {
    return (
      <div className="min-h-screen flex items-center justify-center  px-4">
        <Card className="max-w-md w-full p-8 text-center border-0 shadow-xl bg-white/90 backdrop-blur">
          <div className="flex flex-col items-center space-y-6">
            {/* Animated Loading Spinner */}
            <div className="w-20 h-20 bg-gradient-to-br from-primary-400 to-primary-600 rounded-2xl flex items-center justify-center shadow-lg">
              <Loader2 className="w-10 h-10 text-white animate-spin" />
            </div>
            <h2 className="text-2xl font-semibold">
              Redirecting to App Store
            </h2>
            <p className="text-muted-foreground">
              Please wait while we redirect you to download the app...
            </p>
          </div>
        </Card>
      </div>
    );
  }

  if (isMobile) {
    return (
      <div className="min-h-screen flex items-center justify-center  px-4">
        <Card className="max-w-md w-full p-8 border-0 shadow-xl bg-white/90 backdrop-blur">
          <div className="flex flex-col items-center space-y-6">
            {/* Mobile Icon */}
            <div className="w-20 h-20 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl flex items-center justify-center shadow-lg">
              <Smartphone className="w-10 h-10 text-white" />
            </div>

            <div className="text-center space-y-2">
              <h2 className="text-2xl font-semibold">
                Open App or Download
              </h2>
              <p className="text-muted-foreground text-sm">
                If the app didn't open automatically, please use the button below to download it.
              </p>
            </div>

            <div className="w-full">
              {detectedPlatform === 'ios' && (
                <Button
                  onClick={() => handleManualRedirect('ios')}
                  className="w-full bg-primary-500 hover:bg-primary-600 text-white font-medium flex items-center justify-center gap-3"
                >
                  <FaApple className="w-5 h-5" />
                  App Store
                </Button>
              )}
              {detectedPlatform === 'android' && (
                <Button
                  onClick={() => handleManualRedirect('android')}
                  className="w-full bg-primary-500 hover:bg-primary-600 text-white font-medium flex items-center justify-center gap-3"
                >
                  <FaGooglePlay className="w-5 h-5" />
                  Google Play
                </Button>
              )}
              {!detectedPlatform && (
                <>
                  <Button
                    onClick={() => handleManualRedirect('ios')}
                    className="w-full bg-primary-500 hover:bg-primary-600 text-white font-medium flex items-center justify-center gap-3"
                  >
                    <FaApple className="w-5 h-5" />
                    App Store
                  </Button>
                  <Button
                    onClick={() => handleManualRedirect('android')}
                    className="w-full bg-primary-500 hover:bg-primary-600 text-white font-medium flex items-center justify-center gap-3"
                  >
                    <FaGooglePlay className="w-5 h-5" />
                    Google Play
                  </Button>
                </>
              )}

              <Button
                onClick={() => navigate("/")}
                variant="outline"
                className="w-full h-10 mt-4 border-primary-500 text-primary-500 hover:text-primary-500"
              >
                <Home className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  // Desktop device - show message
  return (
    <div className="min-h-screen flex items-center justify-center  px-4">
      <Card className="max-w-lg w-full p-8 border-0 shadow-xl bg-white/90 backdrop-blur">
        <div className="flex flex-col items-center space-y-6">
          {/* Warning Icon */}
          <div className="w-20 h-20 bg-gradient-to-br from-primary-400 to-primary-600 rounded-2xl flex items-center justify-center shadow-lg">
            <AlertTriangle className="w-10 h-10 text-white" />
          </div>

          <div className="text-center space-y-2">
            <h2 className="text-2xl font-semibold">
              App Not Found
            </h2>
            <p className="text-muted-foreground">
              This link is only available on mobile devices. Please open it on your smartphone.
            </p>
          </div>

          <div className="w-full space-y-3">
            <div className="flex gap-3">
              <Button
                onClick={() => window.open('https://apps.apple.com/app/KD259G8M9Z', '_blank')}
                variant="outline"
                className="flex-1 h-11 flex items-center justify-center gap-2 border-primary-500 text-primary-500 hover:text-primary-500"
              >
                <FaApple className="w-4 h-4" />
                Open App Store
              </Button>
              <Button
                onClick={() => window.open('https://play.google.com/store/apps/details?id=de.kalialab.kaliaapp', '_blank')}
                variant="outline"
                className="flex-1 h-11 flex items-center justify-center gap-2 border-primary-500 text-primary-500 hover:text-primary-500"
              >
                <FaGooglePlay className="w-4 h-4" />
                Open Google Play
              </Button>
            </div>

            <Button
              onClick={() => navigate("/")}
              className="w-full h-11 bg-primary-600 hover:bg-primary-700 text-white"
            >
              <Home className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default AppRedirection;